import { useState, useCallback, useEffect, useMemo } from 'react';
import { fetchProjects, fetchProjectBoreholes, fetchBoreholeWithLayers, fetchProjectLayerColors, fetchBoreholeUserDataVersions, fetchExecutedBoreholeIds } from '../firebase/boreholeBrowser';

// Fence diagram: stały zestaw 5 linii. Każda linia może mieć osobny zbiór otworów.
// Linia 1 jest "główna" — tylko ona persystuje się do Firestore.
export const LINE_IDS = [1, 2, 3, 4, 5];
const EMPTY_SELECTIONS = LINE_IDS.reduce((acc, id) => ({ ...acc, [id]: [] }), {});

/**
 * Hook do przegladania i wybierania otworow z kolekcji currentProjects.
 *
 * Selekcja podzielona na 5 stałych linii (fence diagram). Aktywna linia decyduje
 * gdzie idą nowe zaznaczenia z mapy/listy. Otwór moze być w wielu liniach.
 */
export function useBoreholeBrowser() {
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [projectsLoaded, setProjectsLoaded] = useState(false);

  const [selectedProject, setSelectedProject] = useState(null);
  const [boreholes, setBoreholes] = useState([]);
  const [loadingBoreholes, setLoadingBoreholes] = useState(false);
  const [boreholesError, setBoreholesError] = useState(null);

  const [layerColors, setLayerColors] = useState({});

  const [projectFilter, setProjectFilter] = useState('');
  const [boreholeFilter, setBoreholeFilter] = useState('');

  // Selekcje per linia: { 1: [...], 2: [...], ..., 5: [...] }
  // Element = { id, distanceOverride: number|null, userDataIdOverride: string|null }
  const [selectionsByLine, setSelectionsByLine] = useState(EMPTY_SELECTIONS);
  const [activeLineId, setActiveLineId] = useState(1);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);

  // Lazy cache wersji UserData per otwór
  const [versionsByBoreholeId, setVersionsByBoreholeId] = useState({});

  // "Wykonane" = otwór ma jakikolwiek wpis w podkolekcji UserData.
  // executedIds === null oznacza "jeszcze nie sprawdzone" (sprawdzamy dopiero
  // po włączeniu filtra — to jedno zapytanie na otwór, patrz fetchExecutedBoreholeIds).
  const [onlyExecuted, setOnlyExecuted] = useState(false);
  const [executedIds, setExecutedIds] = useState(null);
  const [loadingExecuted, setLoadingExecuted] = useState(false);

  const loadBoreholeVersions = useCallback(async (boreholeId) => {
    if (!selectedProject || !boreholeId) return;
    const existing = versionsByBoreholeId[boreholeId];
    if (existing && (existing.loading || existing.versions)) return;
    setVersionsByBoreholeId(prev => ({ ...prev, [boreholeId]: { loading: true, versions: null, error: null } }));
    try {
      const versions = await fetchBoreholeUserDataVersions(selectedProject.id, boreholeId);
      setVersionsByBoreholeId(prev => ({ ...prev, [boreholeId]: { loading: false, versions, error: null } }));
    } catch (err) {
      console.error('Blad ladowania wersji UserData:', err);
      setVersionsByBoreholeId(prev => ({ ...prev, [boreholeId]: { loading: false, versions: null, error: 'Nie udalo sie zaladowac wersji.' } }));
    }
  }, [selectedProject, versionsByBoreholeId]);

  const [crossSectionSettings, setCrossSectionSettings] = useState(null);
  const [pendingHandoffApply, setPendingHandoffApply] = useState(null);

  // Aktywna selekcja (back-compat dla istniejacych konsumentów hooka)
  const orderedSelection = selectionsByLine[activeLineId] || [];

  // Sety ID per linia (zwraca object { 1: Set, 2: Set, ... 5: Set })
  const selectedBoreholeIdsByLine = useMemo(() => {
    const out = {};
    for (const id of LINE_IDS) {
      out[id] = new Set((selectionsByLine[id] || []).map(s => s.id));
    }
    return out;
  }, [selectionsByLine]);
  const selectedBoreholeIds = selectedBoreholeIdsByLine[activeLineId] || new Set();

  // Helper — mutator selekcji aktywnej linii
  const updateActiveLine = useCallback((updater) => {
    setSelectionsByLine(prev => ({
      ...prev,
      [activeLineId]: typeof updater === 'function' ? updater(prev[activeLineId] || []) : updater,
    }));
  }, [activeLineId]);

  // Helper — mutator dowolnej linii (np. dla pigułek 1..5 w liście)
  const updateLine = useCallback((lineId, updater) => {
    setSelectionsByLine(prev => ({
      ...prev,
      [lineId]: typeof updater === 'function' ? updater(prev[lineId] || []) : updater,
    }));
  }, []);

  const loadProjects = useCallback(async () => {
    if (projectsLoaded) return;
    setLoadingProjects(true);
    setProjectsError(null);
    try {
      const data = await fetchProjects();
      setProjects(data);
      setProjectsLoaded(true);
    } catch (err) {
      console.error('Blad ladowania projektow:', err);
      setProjectsError('Nie udalo sie zaladowac projektow.');
    } finally {
      setLoadingProjects(false);
    }
  }, [projectsLoaded]);

  const selectProject = useCallback(async (project) => {
    if (selectedProject?.id === project.id) {
      setSelectedProject(null);
      setBoreholes([]);
      setVersionsByBoreholeId({});
      return;
    }
    setSelectedProject(project);
    setSelectionsByLine(EMPTY_SELECTIONS);
    setActiveLineId(1);
    setVersionsByBoreholeId({});
    setExecutedIds(null); // nowy projekt = sprawdzamy "wykonane" od nowa
    setLoadingBoreholes(true);
    setBoreholesError(null);
    setBoreholeFilter('');
    try {
      const [data, colors] = await Promise.all([
        fetchProjectBoreholes(project.id),
        fetchProjectLayerColors(project.id),
      ]);
      setBoreholes(data);
      setLayerColors(colors);
    } catch (err) {
      console.error('Blad ladowania otworow:', err);
      setBoreholesError('Nie udalo sie zaladowac otworow.');
    } finally {
      setLoadingBoreholes(false);
    }
  }, [selectedProject]);

  const refreshProjects = useCallback(async () => {
    setProjectsLoaded(false);
    setSelectedProject(null);
    setBoreholes([]);
    setSelectionsByLine(EMPTY_SELECTIONS);
    setActiveLineId(1);
    setLoadingProjects(true);
    setProjectsError(null);
    try {
      const data = await fetchProjects();
      setProjects(data);
      setProjectsLoaded(true);
    } catch (err) {
      console.error('Blad ladowania projektow:', err);
      setProjectsError('Nie udalo sie zaladowac projektow.');
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  // Handoff z apki zewnętrznej idzie zawsze do Linii 1 (głównej).
  const applyHandoff = useCallback((handoff) => {
    if (!handoff || !Array.isArray(handoff.items) || handoff.items.length === 0) return;
    const firstItem = handoff.items[0];
    if (!firstItem.currentProjectId) return;

    setCrossSectionSettings(handoff.crossSection || null);
    setPendingHandoffApply(handoff);

    if (selectedProject?.id !== firstItem.currentProjectId) {
      selectProject({ id: firstItem.currentProjectId, title: firstItem.projectName || '(brak nazwy)' });
    }
  }, [selectedProject, selectProject]);

  useEffect(() => {
    if (!pendingHandoffApply) return;
    if (!selectedProject) return;
    if (loadingBoreholes) return;

    const items = pendingHandoffApply.items;
    if (!items || items.length === 0) {
      setPendingHandoffApply(null);
      return;
    }
    if (selectedProject.id !== items[0].currentProjectId) return;

    setSelectionsByLine(prev => ({
      ...prev,
      1: items.map((item, i) => ({
        id: item.infoBoreholeId,
        distanceOverride: i === 0 ? null : (parseFloat(item.distance) || null),
        userDataIdOverride: null,
      })),
    }));
    setActiveLineId(1);
    setPendingHandoffApply(null);
  }, [pendingHandoffApply, selectedProject, loadingBoreholes]);

  // Toggle w AKTYWNEJ linii
  const toggleBoreholeSelection = useCallback((boreholeId) => {
    updateActiveLine(prev => {
      const exists = prev.find(s => s.id === boreholeId);
      if (exists) return prev.filter(s => s.id !== boreholeId);
      return [...prev, { id: boreholeId, distanceOverride: null, userDataIdOverride: null }];
    });
  }, [updateActiveLine]);

  // Toggle w KONKRETNEJ linii (dla pigułek 1..5 obok nazwy)
  const toggleBoreholeSelectionInLine = useCallback((boreholeId, lineId) => {
    updateLine(lineId, prev => {
      const exists = prev.find(s => s.id === boreholeId);
      if (exists) return prev.filter(s => s.id !== boreholeId);
      return [...prev, { id: boreholeId, distanceOverride: null, userDataIdOverride: null }];
    });
  }, [updateLine]);

  const clearSelection = useCallback(() => {
    updateActiveLine([]);
  }, [updateActiveLine]);

  const clearAllSelections = useCallback(() => {
    setSelectionsByLine(EMPTY_SELECTIONS);
  }, []);

  const selectAllBoreholes = useCallback(() => {
    updateActiveLine(boreholes.map(b => ({ id: b.id, distanceOverride: null, userDataIdOverride: null })));
  }, [boreholes, updateActiveLine]);

  const addBoreholesByIds = useCallback((ids) => {
    updateActiveLine(prev => {
      const existing = new Set(prev.map(s => s.id));
      const toAdd = ids.filter(id => !existing.has(id));
      return [...prev, ...toAdd.map(id => ({ id, distanceOverride: null, userDataIdOverride: null }))];
    });
  }, [updateActiveLine]);

  const setBoreholeUserDataVersion = useCallback((index, userDataId) => {
    updateActiveLine(prev => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], userDataIdOverride: userDataId || null };
      return next;
    });
  }, [updateActiveLine]);

  const moveBoreholeUp = useCallback((index) => {
    if (index <= 0) return;
    updateActiveLine(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next.map(s => ({ ...s, distanceOverride: null }));
    });
  }, [updateActiveLine]);

  const moveBoreholeDown = useCallback((index) => {
    updateActiveLine(prev => {
      if (index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next.map(s => ({ ...s, distanceOverride: null }));
    });
  }, [updateActiveLine]);

  const setBoreholeDistance = useCallback((index, distance) => {
    updateActiveLine(prev => {
      const next = [...prev];
      next[index] = { ...next[index], distanceOverride: distance };
      return next;
    });
  }, [updateActiveLine]);

  // Helper czysta funkcja — odległości skumulowane dla danej selekcji
  const computeDistancesFor = useCallback((selection) => {
    const boreholeMap = {};
    boreholes.forEach(b => { boreholeMap[b.id] = b; });

    let cumulative = 0;
    return selection.map((sel, index) => {
      if (index === 0) {
        return { ...sel, autoDistance: 0, cumulativeDistance: 0 };
      }
      const prevBh = boreholeMap[selection[index - 1].id];
      const currBh = boreholeMap[sel.id];

      let auto = 20;
      if (prevBh && currBh && prevBh.coordX != null && currBh.coordX != null &&
          prevBh.coordY != null && currBh.coordY != null) {
        const dx = currBh.coordX - prevBh.coordX;
        const dy = currBh.coordY - prevBh.coordY;
        auto = Math.round(Math.sqrt(dx * dx + dy * dy) * 10) / 10;
      }

      const usedDistance = sel.distanceOverride != null ? sel.distanceOverride : auto;
      cumulative += usedDistance;

      return { ...sel, autoDistance: auto, cumulativeDistance: cumulative };
    });
  }, [boreholes]);

  const computedDistancesByLine = useMemo(() => {
    const out = {};
    for (const id of LINE_IDS) {
      out[id] = computeDistancesFor(selectionsByLine[id] || []);
    }
    return out;
  }, [computeDistancesFor, selectionsByLine]);
  const computedDistances = computedDistancesByLine[activeLineId] || [];

  /**
   * Pobierz pelne dane otworow dla wybranej linii (lub aktywnej, gdy lineId=null) i zmapuj na format edytora.
   */
  const importSelectedBoreholes = useCallback(async (lineId = null) => {
    const line = lineId || activeLineId;
    const selection = selectionsByLine[line] || [];
    const distances = computedDistancesByLine[line] || [];
    if (!selectedProject || selection.length === 0) return null;

    setImporting(true);
    setImportError(null);
    try {
      const results = await Promise.all(
        selection.map(sel =>
          fetchBoreholeWithLayers(selectedProject.id, sel.id, layerColors, sel.userDataIdOverride || null)
        )
      );

      const boreholeMap = {};
      boreholes.forEach(b => { boreholeMap[b.id] = b; });

      const editorBoreholes = results.map((bh, index) => {
        const fbBorehole = boreholeMap[selection[index].id];
        return {
          id: index + 1,
          name: bh.name,
          depth: bh.depth,
          distance: distances[index]?.cumulativeDistance || 0,
          x: 0,
          y: 0,
          coordX: fbBorehole?.coordX || null,
          coordY: fbBorehole?.coordY || null,
          waterLevels: bh.waterLevels || [],
          layers: bh.layers.length > 0 ? bh.layers : [
            { id: 1, name: 'Warstwa 1', thickness: 5, color: '#8B4513', pattern: 'none', soilState: '', geotechnicalLayer: '' }
          ],
          sourceProjectId: bh.sourceProjectId,
          sourceBoreholeId: bh.sourceBoreholeId,
          sourceUserDataId: bh.sourceUserDataId,
        };
      });

      return editorBoreholes;
    } catch (err) {
      console.error('Blad importu otworow:', err);
      setImportError('Nie udalo sie pobrac danych otworow.');
      return null;
    } finally {
      setImporting(false);
    }
  }, [selectedProject, activeLineId, selectionsByLine, computedDistancesByLine, boreholes, layerColors]);

  // Sprawdzenie "wykonanych" odpalane leniwie — dopiero gdy user włączy filtr.
  // Wynik trzymany do zmiany projektu (selectProject czyści executedIds).
  useEffect(() => {
    if (!onlyExecuted || !selectedProject || boreholes.length === 0) return;
    if (executedIds !== null) return; // już sprawdzone dla tego projektu

    let cancelled = false;
    setLoadingExecuted(true);
    fetchExecutedBoreholeIds(selectedProject.id, boreholes.map(b => b.id))
      .then(set => { if (!cancelled) setExecutedIds(set); })
      .catch(err => {
        console.error('Blad sprawdzania wykonanych otworow:', err);
        if (!cancelled) setExecutedIds(new Set());
      })
      .finally(() => { if (!cancelled) setLoadingExecuted(false); });

    return () => { cancelled = true; };
  }, [onlyExecuted, selectedProject, boreholes, executedIds]);

  const filteredProjects = projects.filter(p =>
    p.title.toLowerCase().includes(projectFilter.toLowerCase())
  );

  const filteredBoreholes = boreholes.filter(b => {
    if (!(b.nameBorehole || '').toLowerCase().includes(boreholeFilter.toLowerCase())) return false;
    // Dopóki sprawdzenie trwa (executedIds === null) nie ukrywamy nic —
    // lepiej pokazać komplet niż mignąć pustą listą.
    if (onlyExecuted && executedIds && !executedIds.has(b.id)) return false;
    return true;
  });

  return {
    projects: filteredProjects,
    loadingProjects,
    projectsError,
    projectsLoaded,
    loadProjects,
    refreshProjects,
    projectFilter, setProjectFilter,

    selectedProject,
    selectProject,
    boreholes: filteredBoreholes,
    allBoreholes: boreholes,
    loadingBoreholes,
    boreholesError,
    boreholeFilter, setBoreholeFilter,

    // Selection — aktywna linia (back-compat)
    selectedBoreholeIds,
    orderedSelection,
    computedDistances,
    toggleBoreholeSelection,
    clearSelection,
    selectAllBoreholes,
    addBoreholesByIds,
    moveBoreholeUp,
    moveBoreholeDown,
    setBoreholeDistance,
    setBoreholeUserDataVersion,
    versionsByBoreholeId,
    loadBoreholeVersions,
    importSelectedBoreholes,
    importing,
    importError,

    // Filtr "tylko wykonane" (otwory z wpisem w UserData)
    onlyExecuted, setOnlyExecuted,
    executedIds,
    loadingExecuted,

    // Selection — 5 linii (fence diagram)
    LINE_IDS,
    activeLineId,
    setActiveLineId,
    selectionsByLine,
    selectedBoreholeIdsByLine,
    computedDistancesByLine,
    toggleBoreholeSelectionInLine,
    clearAllSelections,

    // Handoff z apki zewnętrznej
    applyHandoff,
    crossSectionSettings,
    pendingHandoffApply,
  };
}
